import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useToast } from '@bonusly/design-system';
import { useEventListener } from '@bonusly/events';
import { useHttpClient } from '@bonusly/http-client';

import { CelebrationType, CelebrationVisibility, CelebrationWorkAnniversaryPointSetting } from '../../schemas/celebrations';
import { CelebrationFormData } from './types';

export const useCelebrationReportCSVExport = () => {
	const [isLoading, setIsLoading] = useState(false);
	const http = useHttpClient();
	const toast = useToast();
	const { t } = useTranslation('celebrations', { keyPrefix: 'admin.export' });

	const handleExportClick = useCallback(async () => {
		if (isLoading) {
			return;
		}

		setIsLoading(true);
		toast({
			title: t('starting'),
			status: 'success',
			duration: 3000,
		});

		try {
			// Step 1: Generate the report
			const reportRes = await http.get('/admin/celebrations_report', {
				headers: { 'Key-Inflection': 'camel' },
			});

			const reportId = reportRes.data.generatedFileId;
			if (!reportId) {
				setIsLoading(false);
				toast({
					title: t('failedGeneration'),
					status: 'error',
					duration: 5000,
				});
			}

			// Step 2: Poll for the report to be ready
			const interval = setInterval(async () => {
				try {
					const pollRes = await http.get(`/admin/celebrations_report/${reportId}`, {
						headers: { 'Key-Inflection': 'camel' },
					});

					if (pollRes.data.generated && pollRes.data.fileUrl) {
						clearInterval(interval);

						// Step 3: Download the file
						window.location.href = pollRes.data.fileUrl;

						// Step 4: Mark as downloaded
						await http.patch(`/admin/celebrations_report/${reportId}`, { downloaded: true }, { headers: { 'Key-Inflection': 'camel' } });

						setIsLoading(false);
						toast({
							title: t('completed'),
							status: 'success',
							duration: 5000,
						});
					}
				} catch (err) {
					clearInterval(interval);
					setIsLoading(false);
					toast({
						title: t('failed'),
						status: 'error',
						duration: 5000,
					});
				}
			}, 5000);
		} catch (err) {
			setIsLoading(false);
			toast({
				title: t('failed'),
				status: 'error',
				duration: 5000,
			});
		}
	}, [http, isLoading, toast, t]);

	return {
		handleExportClick,
		isLoading,
	};
};

const getDefaultCelebrationAmount = (type: CelebrationType) => {
	return {
		[CelebrationType.Birthday]: 50,
		[CelebrationType.NewHire]: 50,
		[CelebrationType.Welcome]: 10,
		[CelebrationType.WorkAnniversary]: 100,
		[CelebrationType.Custom]: 0,
		[CelebrationType.Wellness]: 0,
	}[type];
};

const getDefaultCelebrationReason = (type: CelebrationType) => {
	return {
		[CelebrationType.Birthday]: "Let's celebrate @recipient for their birthday on anniversary_date. Enjoy your day! +amount",
		[CelebrationType.NewHire]: "@recipient we're so excited to have you on our team! 👋 Welcome again! +amount",
		[CelebrationType.Welcome]: "@recipient so glad you're here! Bonusly is where we celebrate and recognize our teammates! +amount",
		[CelebrationType.WorkAnniversary]: "Let's celebrate @recipient for their work anniversary on anniversary_date. Enjoy your day! +amount",
		[CelebrationType.Custom]: '',
		[CelebrationType.Wellness]: '',
	}[type];
};

export const useDefaultFormValue = (type: CelebrationType): CelebrationFormData => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'create.prefilled' });

	return useMemo(() => {
		const resolveDefault = (key: string) => {
			if (type === CelebrationType.Custom) {
				return '';
			}

			return t(key, { context: type });
		};

		const reason = type === CelebrationType.Custom ? t('reason', { context: type }) : getDefaultCelebrationReason(type);

		return {
			celebrationDate: null,
			icon: null,
			image: null,
			headline: resolveDefault('headline'),
			includeBenefactors: false,
			name: resolveDefault('name'),
			amount: getDefaultCelebrationAmount(type),
			reason,
			receivingRulesConditions: [],
			daysToDelaySend: null,
			shiftWeekendCelebrations: true,
			type,
			visibility: CelebrationVisibility.Public,
			yearAmountMappings: [],
			workAnniversaryPointSetting: CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount,
		};
	}, [type, t]);
};

export const useCelebrationEventTracking = () => {
	const { trackEvent } = useAnalytics();

	// NOTE: Event is dispatched in use-create-comment.ts when a comment is created on a celebration bonus
	useEventListener('Celebration Comment Created', (data) => {
		trackEvent(AnalyticsEvent.Celebrations.CommentCreated, data);
	});
};
